package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Space
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlin.math.round
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Button as ButtonElement

private const val THUMB_INSET_PX = 2
private const val SQUEEZE_PRECISION = 1000.0

@Composable
fun Switch(
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    disabled: Boolean = false,
    ariaLabel: String? = null,
    size: ControlSize = ControlSize.Md,
    loading: Boolean = false,
    invalid: Boolean = false,
) {
    val metrics = size.metrics
    val thumb = metrics.switchHeightPx - 2 * THUMB_INSET_PX
    val travel = metrics.switchWidthPx - thumb - 2 * THUMB_INSET_PX
    val squeeze = round((thumb - Motion.SWITCH_SQUEEZE_PX) * SQUEEZE_PRECISION / thumb) / SQUEEZE_PRECISION
    val interactive = !disabled && !loading

    Label(
        attrs = Modifier
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "${Space.s2}")
                property("min-height", "${metrics.height}")
                property("cursor", if (disabled) "not-allowed" else "pointer")
            }
            .then(modifier)
            .toAttrs(),
    ) {
        ButtonElement(
            attrs = Modifier
                .classNames(ControlClass.SWITCH)
                .styleModifier {
                    property(ControlClass.SWITCH_WIDTH_VAR, "${metrics.switchWidthPx}px")
                    property(ControlClass.SWITCH_HEIGHT_VAR, "${metrics.switchHeightPx}px")
                    property(ControlClass.SWITCH_THUMB_VAR, "${thumb}px")
                    property(ControlClass.SWITCH_TRAVEL_VAR, "${travel}px")
                    property(ControlClass.SWITCH_SQUEEZE_VAR, "$squeeze")
                }
                .onClick { event -> if (interactive) onCheckedChange(!checked) else event.preventDefault() }
                .onMouseDown { event -> if (disabled) event.preventDefault() }
                .toAttrs {
                    attr("type", "button")
                    attr("role", "switch")
                    attr("aria-checked", checked.toString())
                    if (disabled) attr("aria-disabled", "true")
                    if (loading) attr("aria-busy", "true")
                    if (invalid) attr("aria-invalid", "true")
                    ariaLabel?.let { attr("aria-label", it) }
                },
        ) {
            Span(
                attrs = Modifier
                    .classNames(ControlClass.SWITCH_THUMB)
                    .styleModifier {
                        if (loading) {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                    }
                    .toAttrs { attr("aria-hidden", "true") },
            ) {
                if (loading) Spinner(sizePx = thumb - 2 * THUMB_INSET_PX, color = Palette.accent, ariaLabel = "")
            }
        }
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .fontSize(metrics.fontSize)
                    .color(Palette.textPrimary),
            )
        }
    }
}
